import type { DataEnvelope, ErrorEnvelope, ListMeta } from '../types/api';

/** `/api/v1` (same origin, dev proxy) or a full URL such as https://backend.easlead.com/api/v1. */
const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL || '/api/v1';

/** Origin of the API: the page's own origin unless VITE_API_BASE_URL is a full URL. */
export function apiOrigin(): string {
  if (/^https?:\/\//.test(API_BASE_URL)) return new URL(API_BASE_URL).origin;
  return typeof window === 'undefined' ? '' : window.location.origin;
}
const CLIENT_HEADER = `web/${import.meta.env.VITE_APP_VERSION ?? '0.1.0'}`;
const CSRF_COOKIE = 'lm_csrf';

/** Every failed API call surfaces as an ApiError with the server's code (or NETWORK_ERROR). */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(code: string, status: number, message: string, details?: unknown, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.requestId = requestId;
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }

  /** Field errors from VALIDATION_FAILED, keyed by the body path without the `body.` prefix. */
  get fieldErrors(): Record<string, string> {
    const issues =
      (this.details as { issues?: { path: string; message: string }[] } | undefined)?.issues ?? [];
    return Object.fromEntries(issues.map((i) => [i.path.replace(/^body\./, ''), i.message]));
  }
}

/**
 * The access token lives only in memory (ADR-010). On reload, or when it expires, the HttpOnly
 * refresh cookie obtains a new one through POST /auth/refresh (with the double-submit CSRF header).
 */
let accessToken: string | null = null;
const expiryListeners = new Set<() => void>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function hasAccessToken(): boolean {
  return accessToken !== null;
}

/** Called when the session can no longer be refreshed (signed out elsewhere, expired, revoked). */
export function onSessionExpired(listener: () => void): () => void {
  expiryListeners.add(listener);
  return () => expiryListeners.delete(listener);
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  idempotencyKey?: string;
  /** Internal: do not attempt a token refresh on 401 (auth endpoints, the refresh call itself). */
  skipRefresh?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query))
    if (value !== undefined && value !== '') params.set(key, String(value));
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as ErrorEnvelope).error?.code === 'string'
  );
}

export function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const entry = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return entry ? decodeURIComponent(entry.slice(name.length + 1)) : undefined;
}

async function send<T>(path: string, options: RequestOptions): Promise<DataEnvelope<T>> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Client': CLIENT_HEADER,
    ...options.headers,
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? null : JSON.stringify(options.body),
      // 'include': the session cookies must also reach an API on another subdomain (backend.easlead.com).
      credentials: 'include',
      signal: options.signal ?? null,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError('NETWORK_ERROR', 0, 'Cannot reach the server. Check your connection and try again.');
  }

  if (response.status === 204) return { data: undefined as T };
  const text = await response.text();
  let payload: unknown = undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = undefined;
    }
  }

  if (!response.ok) {
    if (isErrorEnvelope(payload)) {
      const { code, message, details, requestId } = payload.error;
      throw new ApiError(code, response.status, message, details, requestId);
    }
    throw new ApiError('HTTP_ERROR', response.status, `Request failed with status ${response.status}.`);
  }
  if (payload === undefined || typeof payload !== 'object' || payload === null || !('data' in payload)) {
    throw new ApiError('INVALID_RESPONSE', response.status, 'The server returned an unexpected response.');
  }
  return payload as DataEnvelope<T>;
}

export interface SessionTokens {
  accessToken: string;
  expiresIn: number;
}

let refreshing: Promise<boolean> | null = null;

async function doRefresh(attempt = 0): Promise<boolean> {
  try {
    const res = await send<SessionTokens>('/auth/refresh', {
      method: 'POST',
      headers: { 'X-CSRF-Token': readCookie(CSRF_COOKIE) ?? '' },
      skipRefresh: true,
    });
    setAccessToken(res.data.accessToken);
    return true;
  } catch (err) {
    // Another tab refreshed at the same moment: the cookie now holds the new token, so retry once.
    if (err instanceof ApiError && err.code === 'REFRESH_RACE' && attempt === 0) {
      await new Promise((r) => setTimeout(r, 250));
      return doRefresh(1);
    }
    if (err instanceof ApiError && err.isNetworkError) throw err;
    setAccessToken(null);
    return false;
  }
}

/** Single-flight refresh: concurrent 401s share one refresh request. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= doRefresh().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<DataEnvelope<T>> {
  try {
    return await send<T>(path, options);
  } catch (err) {
    const retryable =
      err instanceof ApiError && err.status === 401 && !options.skipRefresh && accessToken !== null;
    if (!retryable) throw err;
    if (await refreshSession()) return send<T>(path, options);
    for (const listener of expiryListeners) listener();
    throw err;
  }
}

type Opts = Omit<RequestOptions, 'method' | 'body'>;

export const http = {
  async get<T>(path: string, options?: Opts): Promise<T> {
    return (await request<T>(path, { ...options, method: 'GET' })).data;
  },
  async list<T>(path: string, options?: Opts): Promise<{ data: T; meta: ListMeta }> {
    const res = await request<T>(path, { ...options, method: 'GET' });
    return { data: res.data, meta: res.meta ?? {} };
  },
  async post<T>(path: string, body?: unknown, options?: Opts): Promise<T> {
    return (await request<T>(path, { ...options, method: 'POST', body })).data;
  },
  async patch<T>(path: string, body?: unknown, options?: Opts): Promise<T> {
    return (await request<T>(path, { ...options, method: 'PATCH', body })).data;
  },
  async put<T>(path: string, body?: unknown, options?: Opts): Promise<T> {
    return (await request<T>(path, { ...options, method: 'PUT', body })).data;
  },
  async delete<T>(path: string, options?: Opts): Promise<T> {
    return (await request<T>(path, { ...options, method: 'DELETE' })).data;
  },
};
